import { useState } from 'react';
import { Loader2, Search, AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { diagnosticsAPI } from '../../lib/api';

function getErrorMessage(err) {
  return err?.response?.data?.detail || err?.message || 'Request failed';
}

function PredicateRow({ p }) {
  return (
    <tr className={p.passes ? '' : 'bg-red-50 dark:bg-red-950/30'}>
      <td className="px-2 py-1 font-mono text-xs">{p.field}</td>
      <td className="px-2 py-1 font-mono text-xs">{p.predicate_type}</td>
      <td className="px-2 py-1 font-mono text-xs break-all">
        {p.predicate_type === 'regex'
          ? `/${p.expected_pattern}/i`
          : JSON.stringify(p.expected_value)}
      </td>
      <td className="px-2 py-1 font-mono text-xs break-all">
        {JSON.stringify(p.actual_value)}
      </td>
      <td className="px-2 py-1 text-center">
        {p.passes ? (
          <CheckCircle2 className="h-4 w-4 text-green-600 inline" />
        ) : (
          <XCircle className="h-4 w-4 text-red-600 inline" />
        )}
      </td>
    </tr>
  );
}

function FilterComparison({ label, filter, predicates, passes }) {
  if (!filter) return null;
  const isEmpty = Object.keys(filter).length === 0;
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {label}
        </span>
        {passes === true && (
          <Badge className="bg-green-600 text-white border-transparent text-xs">PASS</Badge>
        )}
        {passes === false && (
          <Badge className="bg-red-600 text-white border-transparent text-xs">FAIL</Badge>
        )}
      </div>
      <pre className="text-xs bg-muted rounded p-2 overflow-x-auto font-mono">
        {isEmpty ? '{ }  (no filter, matches everything)' : JSON.stringify(filter, null, 2)}
      </pre>
      {predicates && predicates.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs border">
            <thead>
              <tr className="bg-muted">
                <th className="px-2 py-1 text-left">Field</th>
                <th className="px-2 py-1 text-left">Type</th>
                <th className="px-2 py-1 text-left">Expected</th>
                <th className="px-2 py-1 text-left">Actual</th>
                <th className="px-2 py-1 text-center">Pass</th>
              </tr>
            </thead>
            <tbody>
              {predicates.map((p, i) => (
                <PredicateRow key={i} p={p} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function DocumentAnalysis({ doc }) {
  const failing = doc.failing_predicates || [];
  const hasFailing = failing.length > 0;

  return (
    <div className="border rounded p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <span className="font-mono text-sm font-semibold">{doc.statement_id}</span>
        {hasFailing ? (
          <Badge className="bg-red-600 text-white border-transparent">
            EXCLUDED
          </Badge>
        ) : (
          <Badge className="bg-green-600 text-white border-transparent">
            VISIBLE
          </Badge>
        )}
      </div>

      {/* Verdict */}
      <div className={`flex items-start gap-2 text-sm rounded p-3 ${
        hasFailing
          ? 'bg-red-50 dark:bg-red-950/30 text-red-800 dark:text-red-300'
          : 'bg-green-50 dark:bg-green-950/30 text-green-800 dark:text-green-300'
      }`}>
        {hasFailing ? (
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
        ) : (
          <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
        )}
        <span>{doc.verdict}</span>
      </div>

      {/* Filter-relevant fields */}
      <details>
        <summary className="text-xs font-semibold uppercase tracking-wide text-muted-foreground cursor-pointer">
          Stored fields (filter-relevant)
        </summary>
        <pre className="text-xs bg-muted rounded p-2 mt-2 overflow-x-auto font-mono">
          {JSON.stringify(doc.filter_relevant_fields, null, 2)}
        </pre>
      </details>

      {/* Side-by-side filters */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <FilterComparison
          label="Reconcile filter (finds it)"
          filter={doc.reconcile_filter}
          predicates={doc.reconcile_predicate_results}
          passes={doc.reconcile_passes}
        />
        <FilterComparison
          label="List filter (unfiltered / entity=all)"
          filter={doc.list_filter_unfiltered}
          predicates={doc.list_unfiltered_predicate_results}
          passes={doc.list_unfiltered_passes}
        />
      </div>

      {/* Entity filter tests (auto-detected or user-supplied) */}
      {doc.entity_filter_tests?.length > 0 && (
        <div className="space-y-3">
          {doc.entity_filter_tests.map((et, i) => (
            <FilterComparison
              key={i}
              label={`List filter (entity=${et.entity_id})`}
              filter={et.filter}
              predicates={et.predicate_results}
              passes={et.passes}
            />
          ))}
        </div>
      )}

      {/* Null entity warning */}
      {doc.entity_null_warning && (
        <div className="flex items-start gap-2 text-sm rounded p-3 bg-orange-50 dark:bg-orange-950/30 text-orange-800 dark:text-orange-300">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>{doc.entity_null_warning}</span>
        </div>
      )}

      {/* Failing predicates callout */}
      {hasFailing && (
        <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded p-3 space-y-2">
          <div className="text-xs font-semibold uppercase tracking-wide text-red-700 dark:text-red-400">
            Excluding predicate(s)
          </div>
          {failing.map((p, i) => (
            <div key={i} className="font-mono text-xs">
              <span className="font-semibold">{p.field}:</span>{' '}
              filter expects{' '}
              <span className="text-red-700 dark:text-red-400">
                {p.predicate_type === 'regex'
                  ? `/${p.expected_pattern}/i`
                  : JSON.stringify(p.expected_value)}
              </span>
              , document has{' '}
              <span className="text-red-700 dark:text-red-400">
                {JSON.stringify(p.actual_value)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function StatementListMismatchPanel() {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [entityId, setEntityId] = useState('');
  const [statementId, setStatementId] = useState('');

  const runCheck = async () => {
    setLoading(true);
    setResults(null);
    try {
      const params = {};
      if (entityId.trim()) params.entity_id = entityId.trim();
      if (statementId.trim()) params.statement_id = statementId.trim();
      const { data } = await diagnosticsAPI.statementListMismatch(params);
      setResults(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Statement List Mismatch</CardTitle>
        <p className="text-sm text-muted-foreground">
          Diagnoses why a management statement exists and reconciles but does not appear in the list.
          Compares the list filter and reconcile filter against stored documents, predicate by predicate.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Optional filters */}
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Statement ID (optional)</label>
            <input
              type="text"
              value={statementId}
              onChange={(e) => setStatementId(e.target.value)}
              placeholder="stmt_..."
              className="border rounded px-2 py-1 text-sm font-mono w-56 bg-background"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Entity ID (optional)</label>
            <input
              type="text"
              value={entityId}
              onChange={(e) => setEntityId(e.target.value)}
              placeholder="e.g. eci"
              className="border rounded px-2 py-1 text-sm font-mono w-32 bg-background"
            />
          </div>
          <Button onClick={runCheck} disabled={loading} size="sm">
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Search className="h-4 w-4 mr-2" />
            )}
            Check
          </Button>
        </div>

        {results && (
          <div className="space-y-4">
            {/* Collection summary */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
              <div className="bg-muted rounded p-3">
                <div className="text-muted-foreground text-xs">Total in collection</div>
                <div className="font-mono font-semibold">{results.collection_total}</div>
              </div>
              <div className="bg-muted rounded p-3">
                <div className="text-muted-foreground text-xs">List default limit</div>
                <div className="font-mono font-semibold">{results.list_default_limit}</div>
              </div>
              <div className="bg-muted rounded p-3">
                <div className="text-muted-foreground text-xs">Limit could truncate?</div>
                <div className="font-mono font-semibold">
                  {results.limit_could_truncate ? 'Yes' : 'No'}
                </div>
              </div>
            </div>

            {/* Pennylane overlap */}
            <div className="text-xs bg-muted/50 border rounded p-3">
              <span className="font-semibold">Pennylane filter overlap: </span>
              {results.pennylane_overlap?.shares_filter_code_with_pennylane
                ? <span className="text-red-600 font-semibold">YES (suspect)</span>
                : <span className="text-green-700 dark:text-green-400">None</span>
              }
              <span className="text-muted-foreground ml-1">
                {results.pennylane_overlap?.detail}
              </span>
            </div>

            {/* Documents */}
            {results.documents?.length === 0 && (
              <div className="text-sm text-muted-foreground">
                No documents found. {results.note}
              </div>
            )}

            {results.documents?.map((doc) => (
              <DocumentAnalysis key={doc.statement_id} doc={doc} />
            ))}

            {/* Context note */}
            {results.note && (
              <div className="text-xs text-muted-foreground bg-muted/50 border rounded p-3">
                {results.note}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
